import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useState } from 'react'
import { Menu } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle
} from '@/components/ui/sheet'
import { Logo } from '@/components/generals/logo'
import { useSession } from '@/hooks/use-session'

const NAV = [
    { href: '/#how-it-works', label: 'How it works' },
    { href: '/#features', label: 'Features' },
    { href: '/#templates', label: 'Templates' },
    { href: '/#faq', label: 'FAQ' }
]

export const SiteHeader: FC = (): ReactNode => {
    const { user, ready } = useSession()
    const [open, setOpen] = useState(false)

    return (
        <header className='sticky top-0 z-40 border-b border-border/70 bg-background/85 backdrop-blur supports-backdrop-filter:bg-background/70'>
            <div className='mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-5 sm:px-8'>
                <Logo />

                <nav aria-label='Main' className='hidden items-center gap-1 md:flex'>
                    {NAV.map(item => (
                        <Link key={item.href} href={item.href} className='rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground'>
                            {item.label}
                        </Link>
                    ))}
                </nav>

                <div className={cn('hidden items-center gap-2 transition-opacity md:flex', ready ? 'opacity-100' : 'opacity-0')}>
                    {user ? (
                        <Link href='/posts' className={cn(buttonVariants(), 'h-10 rounded-full px-5')}>
                            Open studio
                        </Link>
                    ) : (
                        <>
                            <Link href='/login' className={cn(buttonVariants({ variant: 'ghost' }), 'h-10 rounded-full px-4')}>
                                Log in
                            </Link>
                            <Link href='/signup' className={cn(buttonVariants(), 'h-10 rounded-full px-5')}>
                                Get started
                            </Link>
                        </>
                    )}
                </div>

                <button
                    type='button'
                    className='inline-flex size-10 items-center justify-center rounded-full border border-border md:hidden'
                    onClick={() => setOpen(true)}
                    aria-label='Open menu'
                >
                    <Menu className='size-5' />
                </button>
            </div>

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side='right' className='w-80'>
                    <SheetHeader>
                        <SheetTitle>Menu</SheetTitle>
                    </SheetHeader>

                    <nav aria-label='Mobile' className='flex flex-col gap-1 px-4'>
                        {NAV.map(item => (
                            <Link key={item.href} href={item.href} onClick={() => setOpen(false)} className='rounded-lg px-3 py-3 text-base font-medium hover:bg-muted'>
                                {item.label}
                            </Link>
                        ))}
                    </nav>

                    <div className='mt-auto flex flex-col gap-2 p-4'>
                        {user ? (
                            <Link href='/posts' className={cn(buttonVariants(), 'h-11 rounded-full')}>
                                Open studio
                            </Link>
                        ) : (
                            <>
                                <Link href='/signup' className={cn(buttonVariants(), 'h-11 rounded-full')}>
                                    Get started
                                </Link>
                                <Link href='/login' className={cn(buttonVariants({ variant: 'outline' }), 'h-11 rounded-full')}>
                                    Log in
                                </Link>
                            </>
                        )}
                    </div>
                </SheetContent>
            </Sheet>
        </header>
    )
}
